import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type * as CSS from 'csstype';
import FocusTrap from 'focus-trap-react';

import { useKeyEvent } from '../../../hooks/useKeyEvent';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { Placement } from '../constants';
import type { ModalContainerProps } from '../types';
import { Size } from '../types';
import { Overlay } from './components/Overlay/Overlay';
import { ModalWrapper } from './ModalContainer.styled';

const getSizeConsideringWindowSize = (
  windowSize: number,
): CSS.Property.MaxHeight | CSS.Property.MaxWidth => {
  const SAFETY_SPACE = 80;
  const maxSizeShouldHave = windowSize - SAFETY_SPACE;

  return `${maxSizeShouldHave}px`;
};

/** ModalContainer name is provisional until the BaseModal is renamed */
export const ModalContainer = ({
  dataCy,
  width,
  height,
  minHeight,
  maxHeight,
  parentElement = document.body,
  close,
  children,
  focusTrapOptions = { initialFocus: false, returnFocusOnDeactivate: false },
  inert,
  transparentBackground = false,
  shouldCloseOnOverlayClick = true,
  placement = Placement.CENTER,
  ...rest
}: ModalContainerProps) => {
  const [oneTrustBanner, setOneTrustBanner] = useState<HTMLElement | null>(null);

  const internalParentElement = parentElement;

  const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
    if (!shouldCloseOnOverlayClick || inert) {
      return;
    }

    const LEFT_CLICK = 0;

    if (!(event instanceof MouseEvent) || event.button === LEFT_CLICK) {
      close?.();
    }
  };

  const modalRef = useOutsideClick(handleOutsideClick);

  const fullHeight = placement !== Placement.CENTER;

  const [internalMaxHeight, setInternalMaxHeight] = useState(
    fullHeight ? '100%' : maxHeight ?? getSizeConsideringWindowSize(window.innerHeight),
  );
  const [internalMaxWidth, setInternalMaxWidth] = useState(
    getSizeConsideringWindowSize(window.innerWidth),
  );

  useEffect(() => {
    const initialOverflow = internalParentElement.style.overflow || '';

    const handleResize = () => {
      setInternalMaxHeight(
        fullHeight
          ? '100%'
          : maxHeight ?? getSizeConsideringWindowSize(window.innerHeight),
      );
      setInternalMaxWidth(getSizeConsideringWindowSize(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);

    internalParentElement.style.overflow = 'hidden';

    return () => {
      internalParentElement.style.overflow = initialOverflow;
      window.removeEventListener('resize', handleResize);
    };
  }, [fullHeight, internalParentElement.style, maxHeight]);

  useKeyEvent('Escape', () => {
    if (!inert) {
      close?.();
    }
  });

  useEffect(() => {
    // OneTrust banner is rendered after the page is loaded, so we need to wait for it to be rendered
    const timeoutId = setTimeout(() => {
      setOneTrustBanner(document.getElementById('onetrust-banner-sdk'));
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const inertProps = {
    inert: inert ? 'true' : undefined,
  };

  return createPortal(
    <Overlay placement={placement}>
      <FocusTrap
        active
        paused={oneTrustBanner ? oneTrustBanner.style.visibility !== 'hidden' : false}
        focusTrapOptions={focusTrapOptions}
      >
        <ModalWrapper
          aria-modal
          aria-label={rest['aria-label']}
          aria-labelledby={rest['aria-labelledby']}
          aria-describedby={rest['aria-describedby']}
          role="dialog"
          data-cy={dataCy}
          ref={modalRef}
          $width={width}
          $maxWidth={internalMaxWidth}
          $height={height}
          $minHeight={minHeight}
          $maxHeight={internalMaxHeight}
          $transparentBackground={transparentBackground}
          $fullHeight={fullHeight}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...inertProps}
        >
          {children}
        </ModalWrapper>
      </FocusTrap>
    </Overlay>,
    internalParentElement,
  );
};

ModalContainer.Size = Size;
ModalContainer.Placement = Placement;

export default ModalContainer;
